
import { useState } from "react";
import { ChevronDown, ChevronRight, Globe, Trash2, Plus, Upload, Palette, Car, Briefcase, GraduationCap, Users, Heart, Utensils, Gamepad2, Home, Scale, Newspaper, Wallet, PawPrint, Church, TestTube, ShoppingBag, Trophy, Sparkles, Computer, Plane, ShieldAlert, Diamond, Cigarette, Frown, OctagonX, Bug, Gamepad } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useWebsiteCategories } from "@/hooks/useWebsiteCategories";
import { WebsiteCategory } from "@/types/websiteCategories";
import { CreateWebsiteCategoryDialog } from "./CreateWebsiteCategoryDialog";
import { BulkUploadCategoriesDialog } from "./BulkUploadCategoriesDialog";

export const WebsiteCategoriesTable = () => {
  const { categories, isLoading, deleteCategory } = useWebsiteCategories();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isBulkUploadDialogOpen, setIsBulkUploadDialogOpen] = useState(false);

  // Map category names to icons
  const getCategoryIcon = (categoryName: string) => {
    const iconMap: { [key: string]: React.ComponentType<any> } = {
      "Arts & Entertainment": Palette,
      "Automotive": Car,
      "Business & Economy": Briefcase,
      "Careers": Briefcase,
      "Education": GraduationCap,
      "Family & Parenting": Users,
      "Health & Medicine (Fitness & Wellness)": Heart,
      "Food & Drink": Utensils,
      "Hobbies & Interests": Gamepad2,
      "Home & Garden": Home,
      "Law, Government & Politics": Scale,
      "News & Media": Newspaper,
      "Personal Finance": Wallet,
      "Pets": PawPrint,
      "Religion & Spirituality": Church,
      "Science": TestTube,
      "Shopping & E-Commerce": ShoppingBag,
      "Sports": Trophy,
      "Style & Fashion": Sparkles,
      "Technology & Computing": Computer,
      "Travel & Tourism": Plane,
      "Real Estate": Home,
      "Dating & Relationships": Heart,
      "Adult Content": ShieldAlert,
      "Gambling": Diamond,
      "Drugs, Alcohol & Tobacco": Cigarette,
      "Hate & Extremism": Frown,
      "Illegal Activities": OctagonX,
      "Malicious or Fraudulent Sites": Bug,
      "Games": Gamepad,
    };
    
    return iconMap[categoryName] || Globe;
  };

  const toggleExpanded = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleDelete = async (category: WebsiteCategory) => {
    if (!confirm(`Are you sure you want to delete "${category.name}"? This will also delete all subcategories.`)) {
      return;
    }
    try {
      await deleteCategory(category.id);
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };


  const renderCategoryRows = (category: WebsiteCategory, level = 0): JSX.Element[] => {
    if (!category || !category.id) {
      console.warn('Invalid category data:', category);
      return [];
    }

    const hasChildren = category.children && Array.isArray(category.children) && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const CategoryIcon = getCategoryIcon(category.name);

    const rows: JSX.Element[] = [];

    // Parent category row
    rows.push(
      <TableRow 
        key={`category-${category.id}`}
        className="hover:bg-gray-50 transition-colors"
      >
        <TableCell className="py-2">
          <div className="flex items-center" style={{ paddingLeft: `${level * 24}px` }}>
            {hasChildren && (
              <button
                onClick={() => toggleExpanded(category.id)}
                className="p-1 hover:bg-gray-200 rounded mr-2"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            )}
            
            <CategoryIcon className="w-5 h-5 text-gray-600 mr-2" />
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{category.name || 'Unnamed Category'}</span>
                {category.is_active === false && (
                  <Badge variant="secondary" className="text-xs">Inactive</Badge>
                )}
              </div>
              {category.description && (
                <div className="text-sm text-gray-500 mt-1">{category.description}</div>
              )}
            </div>
          </div>
        </TableCell>
        <TableCell className="py-2">
          <div className="flex items-center justify-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleDelete(category)}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );

    // Child category rows (if expanded)
    if (hasChildren && isExpanded) {
      category.children!.forEach(child => {
        if (child && child.id) {
          rows.push(...renderCategoryRows(child, level + 1));
        }
      });
    }

    return rows;
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-500">Loading website categories...</div>
      </div>
    );
  }

  // Ensure categories is an array
  const safeCategories = Array.isArray(categories) ? categories : [];

  return (
    <div className="px-6 pb-6 space-y-6">
      {/* Header */}
      <div className="flex items-start gap-2">
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </Button>
        <Button variant="outline" onClick={() => setIsBulkUploadDialogOpen(true)}>
          <Upload className="w-4 h-4 mr-2" />
          Bulk Upload
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <ScrollArea className="w-full">
          <div className="min-w-[600px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[500px]">Category</TableHead>
                  <TableHead className="w-[100px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {safeCategories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center py-12">
                      <Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Website Categories</h3>
                      <p className="text-gray-500 mb-4">
                        Create your first website category to get started.
                      </p>
                      <Button onClick={() => setIsCreateDialogOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Category
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  safeCategories.map(category => {
                    try {
                      return renderCategoryRows(category);
                    } catch (error) {
                      console.error('Error rendering category:', category, error);
                      return (
                        <TableRow key={`error-${category?.id || Math.random()}`}>
                          <TableCell colSpan={2} className="text-center py-4 text-red-500">
                            Error rendering category: {category?.name || 'Unknown'}
                          </TableCell>
                        </TableRow>
                      );
                    }
                  }).flat()
                )}
              </TableBody>
            </Table>
          </div>
        </ScrollArea>
      </div>

      {/* Dialogs */}
      <CreateWebsiteCategoryDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        categories={safeCategories}
      />
      <BulkUploadCategoriesDialog
        open={isBulkUploadDialogOpen}
        onOpenChange={setIsBulkUploadDialogOpen}
        existingCategories={safeCategories}
      />
    </div>
  );
};
